import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/**
 * Generic debounced search input with a leading magnifier icon and a trailing clear
 * button. CVA-compliant — works with `ngModel` and reactive forms. Emits `(search)`
 * after a debounce, plus the standard CVA `onChange` callback synchronously.
 *
 * Distinct from `PageTitleSearchInputComponent`, which is a page-title-integrated
 * search affordance.
 *
 * @example
 * <app-search-input
 *   placeholder="Search students…"
 *   [debounceMs]="250"
 *   (search)="onSearch($event)"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-search-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchInputComponent),
      multi: true,
    },
  ],
  host: { class: 'block' },
  // Suppress the global `:focus-visible` box-shadow ring on the inner input so
  // the focus indicator renders on the container instead (the double-ring
  // below), matching app-input. Component encapsulation gives this rule higher
  // specificity than the global rule, so no `!important` is needed.
  styles: [
    `
      input:focus,
      input:focus-visible {
        box-shadow: none;
        outline: none;
      }
    `,
  ],
  template: `
    <div
      class="flex items-center gap-2 rounded-full border border-outline bg-surface px-3 py-2 transition focus-within:border-[var(--ion-color-primary)] focus-within:shadow-[var(--theme-focus-ring)]"
      [class.opacity-60]="disabled()"
      [class.cursor-not-allowed]="disabled()"
    >
      <app-icon name="search" size="xs" color="medium" />
      <input
        #input
        type="search"
        role="searchbox"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [value]="value()"
        (input)="onInput($event)"
        (blur)="onBlur()"
        [attr.aria-label]="ariaLabel() ?? placeholder()"
        class="flex-1 bg-transparent text-body placeholder:text-placeholder focus:outline-none"
      />
      @if (value()) {
        <button
          type="button"
          aria-label="Clear search"
          (click)="clear()"
          class="shrink-0 rounded-full p-0.5 text-muted hover:text-body focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
        >
          <app-icon name="close-circle" size="xs" />
        </button>
      }
    </div>
  `,
})
export class SearchInputComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('input') private inputEl?: ElementRef<HTMLInputElement>;

  /** Placeholder text. @default 'Search' */
  readonly placeholder = input<string>('Search');

  /** Disables the input. @default false */
  readonly disabled = input<boolean>(false);

  /** Focus the field automatically once the view initializes. @default false */
  readonly autofocus = input<boolean>(false);

  /** Debounce in milliseconds before `(search)` fires. @default 250 */
  readonly debounceMs = input<number>(250);

  /** Accessible label override. Defaults to the placeholder. */
  readonly ariaLabel = input<string | undefined>(undefined);

  /** Fires with the current value after the debounce elapses. */
  readonly search = output<string>();

  protected readonly value = signal<string>('');
  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};
  private debounceTimer: number | null = null;

  ngAfterViewInit(): void {
    if (this.autofocus()) {
      this.focus();
    }
  }

  /** Programmatically focus the search field (e.g. when a hosting modal opens). */
  focus(): void {
    this.inputEl?.nativeElement?.focus();
  }

  writeValue(value: string): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    // Handled via the input signal.
  }

  onInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.value.set(v);
    this.onChange(v);
    this.scheduleSearch(v);
  }

  onBlur(): void {
    this.onTouched();
  }

  clear(): void {
    this.value.set('');
    this.onChange('');
    this.scheduleSearch('');
  }

  private scheduleSearch(v: string): void {
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => {
      this.search.emit(v);
      this.debounceTimer = null;
    }, this.debounceMs());
  }
}
